import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useActiveBundle } from '@/context/active-bundle';
import { AppBundle } from '@/utils/app-bundles';

import { useLocalStorage } from '@/hooks/browser/use-local-storage';

import { Box, SideNav, useSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { shouldShowDevHelper } from '@/layouts/root/components/dev-helper/store';
import { DevToolsNavButton } from './dev-tools-nav-button';
import { TrialCta } from './trial-cta';
import * as styles from './wrapper.css';

export const NAV_COLLAPSED_LOCAL_STORAGE_KEY = 'hydra:is-main-nav-collapsed';

const NAV_WIDTHS = {
  collapsed: Number.parseInt(PlumaSideNavCss.values.collapsedWidth),
  expanded: Number.parseInt(PlumaSideNavCss.values.expandedWidth),
};

/**
 * Publishes the nav's collapsed state to the two places outside React that read
 * it: the localStorage key embedded apps check on boot, and the CSS variable the
 * content column is laid out against.
 */
function useSyncedCollapsedState() {
  const themeContext = useThemeContext();
  const sideNavState = useSideNavState();
  const [, setIsNavCollapsed] = useLocalStorage<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  const isCollapsed = sideNavState?.isCollapsed === true;

  useEffect(() => {
    if (sideNavState != null) {
      setIsNavCollapsed(sideNavState.isCollapsed);
    }
  }, [setIsNavCollapsed, sideNavState]);

  useEffect(() => {
    const navWidth = isCollapsed ? NAV_WIDTHS.collapsed : NAV_WIDTHS.expanded;
    themeContext.setVariable(GlobalCSSVariable.MainNavWidth, `${navWidth}px`);
  }, [isCollapsed, themeContext]);

  return isCollapsed;
}

type MainNavigationWrapperProps = {
  isCollapsible?: boolean;
  children?: React.ReactNode;
};

export function MainNavigationWrapper(props: MainNavigationWrapperProps) {
  const { isCollapsible = true, children } = props;

  const activeApp = useActiveBundle();
  const toggleSideNav = useToggleSideNav();
  const sideNavRef = useRef<HTMLElement>(null);

  const isCollapsed = useSyncedCollapsedState();
  // The edge needs the toggler from a SideNavProvider; settings pages render a
  // fixed, non-collapsible nav whose edge shouldn't respond to clicks at all.
  const canToggle = isCollapsible && toggleSideNav != null;

  return (
    <div className={styles.navContainer}>
      <SideNav
        ref={sideNavRef}
        isCollapsible={isCollapsible}
        isCollapsed={!isCollapsible ? false : undefined}
        overflowY="auto"
        overflowX="hidden"
        aria-label="Main"
        style={{ scrollbarWidth: 'thin' }}
        pt="0"
      >
        {children}

        <Box mt="auto">
          <TrialCta />
          {Object.values(AppBundle).map((bundle) => (
            <div
              key={bundle}
              id={`hydra-main-nav-footer-portal-${bundle}`}
              style={activeApp !== bundle ? { display: 'none' } : {}}
            />
          ))}
          {shouldShowDevHelper() ? <DevToolsNavButton sideNavRef={sideNavRef} isCollapsible={isCollapsible} /> : null}
        </Box>
      </SideNav>

      {/*
        Deliberately not a button: SideNav already renders a labelled
        Collapse/Expand control, so exposing the edge too would announce a
        duplicate of it. role="presentation" keeps the edge out of the
        accessibility tree and out of the tab order, leaving it a pointer-only
        shortcut to the same toggle.
      */}
      {canToggle ? (
        <div
          role="presentation"
          data-test-nav-toggle-edge=""
          className={clsx(styles.toggleEdge, styles.toggleEdgeCursor[isCollapsed ? 'collapsed' : 'expanded'])}
          onClick={toggleSideNav}
        />
      ) : null}
    </div>
  );
}
